import { defineAsyncComponent } from 'vue';
import VueTippy from 'vue-tippy';

// Plugins
import VueCookies from 'vue-cookies';
import FocusLock from '@components/FocusLock';
import { createGmapVuePlugin } from '@gmap-vue/v3';

// Components
import imgBox from '@components/imgBox';
import VideoBox from '@components/VideoBox';
import CmsExposeSettings from '@components/CmsExposeSettings';
import party from '@components/party';
import toggle from '@components/toggle';
import slick from '@components/SlickCarouselSsr';
import ResponsiveImage from '@components/ResponsiveImage';
import AddPromo from '@components/AddPromo';
import MrSpinnerVeil from '@components/MrSpinnerVeil';
import TransitionExpand from '@components/TransitionExpand';
import { VueSvgIconPlugin } from '@yzfe/vue-svgicon';
import Vue3SocialSharingPlugin from "vue3-social-sharing";
// Components
import MrNavigation from "@components/MrNavigation";
import ChatWidget from "@components/ChatWidget";
import AppModal from "@components/AppModal";
import SiteMessageBannerCarousel from '@components/SiteMessageBannerCarousel';
import SiteMessageBanner from '@components/SiteMessageBanner';
import toggleInterval from '@components/toggleInterval';
import interval from '@components/interval';
import EmailCaptureModal from '@components/EmailCapture/EmailCaptureModal';
import PhoneCaptureModal from '@components/EmailCapture/PhoneCaptureModal';
import Notifications from '@components/Notifications';
import StickyPromoDrawer from '@components/StickyPromoDrawer';

const MrBday = defineAsyncComponent(() => import('@components/MrBday'));
const Breadcrumbs = defineAsyncComponent(() => import('@components/Breadcrumbs'));
const SlickCarouselSsr = defineAsyncComponent(() => import('@components/SlickCarouselSsr'));
const SimpleCarouselSSR = defineAsyncComponent(() => import('@components/SimpleCarouselSSR'));
const ReactiveToItemsCarousel = defineAsyncComponent(() => import('@components/Carousels/ReactiveToItemsCarousel'));
const JustForBestExperience = defineAsyncComponent(() => import(/* webpackChunkName: 'JustForBestExperience' */ '@components/Experience/JustForBestExperience'));
const HairColorSimulator = defineAsyncComponent(() => import(/* webpackChunkName: 'HairColorSimulator' */ '@components/AugmentedReality/HairColorSimulator'));
const ReinventedAccordion = defineAsyncComponent(() => import(/* webpackChunkName: 'ReinventedAccordion' */ '@components/ReinventedAccordion/ReinventedAccordion'));
const ClosestHairColorBar = defineAsyncComponent(() => import(/* webpackChunkName: 'ClosestHairColorBar' */ '@components/ClosestHairColorBar'));
const UnlimitedRootsMembershipPricing = defineAsyncComponent(() => import(/* webpackChunkName: 'UnlimitedRootsMembershipPricing' */ '@components/UnlimitedRootsMembershipPricing'));
const StoreValue = defineAsyncComponent(() => import(/* webpackChunkName: 'StoreValue' */ '@components/StoreValue'));
const HelpCenter = defineAsyncComponent(() => import(/* webpackChunkName: 'HelpCenter' */ '@components/HelpCenter'));
const Accordion = defineAsyncComponent(() => import(/* webpackChunkName: 'Accordion' */ '@components/Accordion/Accordion'));
const AccordionSection = defineAsyncComponent(() => import(/* webpackChunkName: 'AccordionSection' */ "@components/Accordion/AccordionSection"));
const Tabs = defineAsyncComponent(() => import(/* webpackChunkName: 'Tabs' */ "@components/Tabs"));
const Faqs = defineAsyncComponent(() => import(/* webpackChunkName: 'Faqs' */ "@components/Faqs"));
const Reviews = defineAsyncComponent(() => import(/* webpackChunkName: 'Reviews' */ "@components/PDP/Reviews"));

// Blog
const BlogSearch = defineAsyncComponent(() => import('@components/Blog/BlogSearch'));
const BlogSocialNetworks = defineAsyncComponent(() => import('@components/Blog/BlogSocialNetworks'));
const RelatedProducts = defineAsyncComponent(() => import('@components/Blog/RelatedProducts'));
const BlogDisqus = defineAsyncComponent(() => import('@components/Blog/BlogDisqus'));
const BlogPosts = defineAsyncComponent(() => import('@components/Blog/BlogPosts'));

// Homepage
const ReturnUserModule = defineAsyncComponent(() => import(/* webpackChunkName: "HomePage" */ '@components/HomePage/ReturnUserModule'));
const UgcWithReviews = defineAsyncComponent(() => import(/* webpackChunkName: 'HomePage' */ '@components/HomePage/UgcWithReviews'));
const ContactUs = defineAsyncComponent(() => import(/* webpackChunkName: 'HomePage' */ '@components/HomePage/ContactUs'));
const HomeHero = defineAsyncComponent(() => import(/* webpackChunkName: "HomePage" */ '@components/HomePage/HomeHero'));
const WhyMR = defineAsyncComponent(() => import(/* webpackChunkName: 'HomePage' */ '@components/HomePage/WhyMR'));
const HomeBlogSection = defineAsyncComponent(() => import(/* webpackChunkName: 'HomePage' */ '@components/HomePage/HomeBlogSection'));
const UgcHomeCarousel = defineAsyncComponent(() => import(/* webpackChunkName: 'UgcHomeCarousel' */ '@components/UgcHomeCarousel'));
const ProductsSwatchesWithUgc = defineAsyncComponent(() => import(/* webpackChunkName: 'ProductsSwatchesWithUgc'*/ '@components/ProductsSwatchesWithUgc'));

// Footer
const MrFooter = defineAsyncComponent(() => import('@components/MrFooter/MrFooter'));
const MrNewsletterSubscription = defineAsyncComponent(() => import('@components/MrNewsletterSubscription'));

// Hair Color Bar
const HairColorBarGuestWifi = defineAsyncComponent(() => import(/* webpackChunkName: 'HairColorBarGuestWifi' */ '@components/HairColorBar/GuestWifi/HairColorBarGuestWifi'));
const HcbServicesOffered = defineAsyncComponent(() => import('@components/HcbServicesOffered'));
const HcbLocationPage = defineAsyncComponent(() => import('@components/HairColorBar/HcbLocationPage'));
const ColorBarMapSectionV2 = defineAsyncComponent(() => import('@components/ColorBarMapSectionV2'));
const SelectLocationMap = defineAsyncComponent(() => import('@components/Locations/SelectLocationMap'));
const ColorBarMapSectionV3 = defineAsyncComponent(() => import('@components/ColorBarMapSectionV3'));
const ColorBarMapSectionV4 = defineAsyncComponent(() => import('@components/ColorBarMapSectionV4'));
const ColorBarMapSection = defineAsyncComponent(() => import('@components/ColorBarMapSection'));
const LocationsDirectoryV2 = defineAsyncComponent(() => import(/* webpackChunkName: 'ColorBarMapSectionV3' */ '@components/ColorBarMapSectionV3/LocationsDirectoryV2'));
const ExpectationsOrLastLocations = defineAsyncComponent(() => import(/* webpackChunkName: 'ColorBarMapSectionV3' */ '@components/ColorBarMapSectionV3/ExpectationsOrLastLocations'));
const ExtoleTag = defineAsyncComponent(() => import(/* webpackChunkName: 'ExtoleTag' */ '@components/ExtoleTag'));
// Shop
const ShopProductCategory = defineAsyncComponent(() => import(/* webpackChunkName: 'Shop' */ '@components/Shop/ShopProductCategory'));
const ShopByColor = defineAsyncComponent(() => import('@components/ShopByColor'));
const FeaturedServicesV2 = defineAsyncComponent(() => import(/* webpackChunkName: 'FeaturedServices' */ '@components/FeaturedServicesV2'));

// Misc
const ToggleableCards = defineAsyncComponent(() => import(/* webpackChunkName: 'ToggleableCards' */ '@components/Cards/ToggleableCards'));
const CardsCarousel = defineAsyncComponent(() => import(/* webpackChunkName: 'CardsCarousel' */ '@components/Carousels/CardsCarousel'));
const CardFlips = defineAsyncComponent(() => import(/* webpackChunkName: "CardFlips" */ '@components/CardFlips'));
const CmsPartialSsr = defineAsyncComponent(() => import(/* webpackChunkName: 'CmsPartial' */ '@components/CmsPartialSsr'));

const EmailCaptureForm = defineAsyncComponent(() => import(/* webpackChunkName: 'EmailCaptureForm' */ '@components/EmailCapture/EmailCaptureForm'));
const ColorWonderHero = defineAsyncComponent(() => import(/* webpackChunkName: 'ColorWonderHero' */ '@components/ColorWonder/ColorWonderHero'));
const ColorwonderExpectations = defineAsyncComponent(() => import(/* webpackChunkName: 'ColorWonderLanding' */ '@components/ColorwonderExpectations'));
const MrBtn = defineAsyncComponent(() => import(/* webpackChunkName: 'MrBtn' */ "@components/MrBtn"));
const MrIcon = defineAsyncComponent(() => import(/* webpackChunkName: 'MrIcon' */ "@components/MrIcon"));
const LazyComponent = defineAsyncComponent(() => import(/* webpackChunkName: 'LazyComponent' */ "@components/LazyComponent"));
const MrMarquee = defineAsyncComponent(() => import(/* webpackChunkName: 'MrMarquee' */ "@components/MrMarquee"));
const ComboBox = defineAsyncComponent(() => import(/* webpackChunkName: 'ComboBox' */ "@components/ComboBox"));

const BtnBuyOrReactivateMemb = defineAsyncComponent(() => import(/* webpackChunkName: 'BtnBuyOrReactivateMemb' */ '@components/BtnBuyOrReactivateMemb'));
const DashHudsonWidget = defineAsyncComponent(() => import(/* webpackChunkName: 'DashHudsonWidget' */ '@components/DashHudsonWidget'));
const ExpectationModule = defineAsyncComponent(() => import(/* webpackChunkName: 'LocationSpecific' */ "@components/LocationSpecific/ExpectationModule"));
const stars = defineAsyncComponent(() => import(/* webpackChunkName: 'stars' */ '@components/stars'));
const toggleHeight = defineAsyncComponent(() => import(/* webpackChunkName: 'toggleHeight' */ '@components/toggleHeight'));

const SierraStartChatBtn = defineAsyncComponent(() => import(/* webpackChunkName: 'SierraStartChatBtn' */ '@components/SierraStartChatBtn'));
const NoDeadEndCtas = defineAsyncComponent(() => import(/* webpackChunkName: 'NoDeadEndCtas' */ '@components/PDP/ColorKitPdpV2/NoDeadEndCtas'));


// Mixins
import globalMixins from '@mixins/globalMixins';
import cartMixin from "@mixins/cartMixin";
import mainAppMixin from "@mixins/mainAppMixin";
import browserContextMixin from "@mixins/browserContextMixin";

// Filters
import filtersPlugin from '../plugins/filtersPlugin.js';

// Directives
import mrScrollAnimation from "../directives/mrScrollAnimation";
import clickOutside from "../directives/clickOutside";
import vRipple from "../directives/vRipple";

const MultiSelectDropdown = defineAsyncComponent(() => import(/* webpackChunkName: 'MultiSelectDropdown' */ '@components/MultiSelectDropdown'));
const CmsPartial = defineAsyncComponent(() => import(/* webpackChunkName: 'CMSPartial' */ '@components/CMSPartial'));



/**
 * Registers global mixins, components and plugins only once, otherwise we can create memory leaks
 * https://github.com/vuejs/vue/issues/5089#issuecomment-284260111
 *
 * @param {import('vue').App} app
 */
export default function registerGlobalsSsr(app) {

  app.use(VueCookies);
  app.use(filtersPlugin);
  app.use(Vue3SocialSharingPlugin);
  app.use(createGmapVuePlugin({
    dynamicLoad: true,
  }));
  app.use(VueTippy, {
    directive: 'tippy', // => v-tippy
    flipDuration: 0,
    popperOptions: {
      modifiers: {
        preventOverflow: {
          enabled: false,
        },
        hide: {
          enabled: false
        }
      },
    },
  });

  app.mixin(globalMixins);
  app.mixin(cartMixin);
  app.mixin(mainAppMixin);
  app.mixin(browserContextMixin);


  app.component('focus-lock', FocusLock);
  app.component('mr-icon', MrIcon);
  app.component('img-box', imgBox);
  app.component('VideoBox', VideoBox);
  app.component('cms-expose-settings', CmsExposeSettings);
  app.component('party', party);
  app.component('toggle', toggle);
  app.component('slick', slick);
  app.component('responsive-image', ResponsiveImage);
  app.component('AddPromo', AddPromo);
  app.component('MrSpinnerVeil', MrSpinnerVeil);
  app.component('TransitionExpand', TransitionExpand);
  app.component('Stars', stars);
  app.component('ToggleHeight', toggleHeight);
  app.component('MultiSelectDropdown', MultiSelectDropdown);
  app.component('CmsPartial', CmsPartial);
  app.component('SierraStartChatBtn', SierraStartChatBtn);

  // Components from SsrApp.vue
  app.component('MrNavigation', MrNavigation);
  app.component('ChatWidget', ChatWidget);
  app.component('AppModal', AppModal);
  app.component('SiteMessageBannerCarousel', SiteMessageBannerCarousel);
  app.component('SiteMessageBanner', SiteMessageBanner);
  app.component('toggleInterval', toggleInterval);
  app.component('interval', interval);
  app.component('EmailCaptureModal', EmailCaptureModal);
  app.component('PhoneCaptureModal', PhoneCaptureModal);
  app.component('Notifications', Notifications);
  app.component('StickyPromoDrawer', StickyPromoDrawer);
  app.component('MrBday', MrBday);
  app.component('Breadcrumbs', Breadcrumbs);
  app.component('SlickCarouselSsr', SlickCarouselSsr);
  app.component('SimpleCarouselSSR', SimpleCarouselSSR);
  app.component('ReactiveToItemsCarousel', ReactiveToItemsCarousel);
  app.component('JustForBestExperience', JustForBestExperience);
  app.component('HairColorSimulator', HairColorSimulator);
  app.component('ReinventedAccordion', ReinventedAccordion);
  app.component('ClosestHairColorBar', ClosestHairColorBar);
  app.component('UnlimitedRootsMembershipPricing', UnlimitedRootsMembershipPricing);
  app.component('StoreValue', StoreValue);
  app.component('HelpCenter', HelpCenter);
  app.component('Accordion', Accordion);
  app.component('AccordionSection', AccordionSection);
  app.component('Tabs', Tabs);
  app.component('Faqs', Faqs);
  app.component('Reviews', Reviews);
  app.component('BlogSearch', BlogSearch);
  app.component('BlogSocialNetworks', BlogSocialNetworks);
  app.component('RelatedProducts', RelatedProducts);
  app.component('BlogDisqus', BlogDisqus);
  app.component('BlogPosts', BlogPosts);
  app.component('ReturnUserModule', ReturnUserModule);
  app.component('UgcWithReviews', UgcWithReviews);
  app.component('ContactUs', ContactUs);
  app.component('HomeHero', HomeHero);
  app.component('WhyMR', WhyMR);
  app.component('HomeBlogSection', HomeBlogSection);
  app.component('UgcHomeCarousel', UgcHomeCarousel);
  app.component('ProductsSwatchesWithUgc', ProductsSwatchesWithUgc);
  app.component('MrFooter', MrFooter);
  app.component('MrNewsletterSubscription', MrNewsletterSubscription);
  app.component('HairColorBarGuestWifi', HairColorBarGuestWifi);
  app.component('HcbServicesOffered', HcbServicesOffered);
  app.component('HcbLocationPage', HcbLocationPage);
  app.component('ColorBarMapSectionV2', ColorBarMapSectionV2);
  app.component('SelectLocationMap', SelectLocationMap);
  app.component('ColorBarMapSectionV3', ColorBarMapSectionV3);
  app.component('ColorBarMapSectionV4', ColorBarMapSectionV4);
  app.component('ColorBarMapSection', ColorBarMapSection);
  app.component('LocationsDirectoryV2', LocationsDirectoryV2);
  app.component('ExpectationsOrLastLocations', ExpectationsOrLastLocations);
  app.component('ShopProductCategory', ShopProductCategory);
  app.component('ShopByColor', ShopByColor);
  app.component('FeaturedServicesV2', FeaturedServicesV2);
  app.component('ToggleableCards', ToggleableCards);
  app.component('CardsCarousel', CardsCarousel);
  app.component('CardFlips', CardFlips);
  app.component('CmsPartialSsr', CmsPartialSsr);
  app.component('EmailCaptureForm', EmailCaptureForm);
  app.component('ColorWonderHero', ColorWonderHero);
  app.component('ColorwonderExpectations', ColorwonderExpectations);
  app.component('MrBtn', MrBtn);
  app.component('LazyComponent', LazyComponent);
  app.component('MrMarquee', MrMarquee);
  app.component('ComboBox', ComboBox);
  app.component('BtnBuyOrReactivateMemb', BtnBuyOrReactivateMemb);
  app.component('DashHudsonWidget', DashHudsonWidget);
  app.component('ExpectationModule', ExpectationModule);
  app.component('ExtoleTag', ExtoleTag);
  app.component('NoDeadEndCtas', NoDeadEndCtas);

  app.directive('mrscrollanimation', mrScrollAnimation);
  app.directive('click-outside', clickOutside);
  app.directive('ripple', vRipple);

  // We install `vue-svgicon` as plugin
  // and configure it to prefix all CSS
  // classes with `mr-icon-`.
  app.use(VueSvgIconPlugin, {
    tagName: 'svgicon',
    classPrefix: 'mr'
  });
}
